import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { IconName } from '@/components/Icon/Icon';
import ActionCards, {
  type ActionCardsProps,
} from '@/components/TopLevelComponents/ActionCards/ActionCards';
import { ContentStyle } from '@/contentful/contentful-exports';
import { brandColors } from '@/styles/theme/values/brandColors';
import type { DynamicRedirectPageCF } from '@/types';
import { detectOS, OS } from '@/utils/detectOS';

interface Props {
  content: DynamicRedirectPageCF;
}

function createActionCardsProps(
  fallbackUrl: string,
  iOSUrl: string | undefined,
  androidUrl: string | undefined
): ActionCardsProps {
  const actionCardsProps = {
    style: ContentStyle.OnLight,
    cards: [
      {
        iconName: IconName.EXTERNAL_LINK,
        heading: 'Not on mobile?',
        subheading: 'Learn more',
        cta: { text: 'Fallback', url: fallbackUrl },
      },
    ],
  };

  if (iOSUrl) {
    actionCardsProps.cards.push({
      iconName: IconName.EXTERNAL_LINK,
      heading: 'iOS Device',
      subheading: 'Install the Sunrun app from the App Store',
      cta: {
        text: 'iOS',
        url: iOSUrl,
      },
    });
  }

  if (androidUrl) {
    actionCardsProps.cards.push({
      iconName: IconName.EXTERNAL_LINK,
      heading: 'Android Device',
      subheading: 'Install the Sunrun app from the Google Play Store',
      cta: { text: 'Android', url: androidUrl },
    });
  }

  return actionCardsProps;
}

export default function DynamicRedirectPage({
  content: {
    fields: { fallbackUrl, iOsUrl, androidUrl },
  },
}: Props) {
  const router = useRouter();
  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    const userOS = detectOS();
    const redirectLocation: Record<OS, string> = {
      [OS.ANDROID]: androidUrl || fallbackUrl,
      [OS.IOS]: iOsUrl || fallbackUrl,
      [OS.OTHER]: fallbackUrl,
    };
    router.push(redirectLocation[userOS]);
  }, [router]);

  const actionCardsProps = createActionCardsProps(
    fallbackUrl,
    iOsUrl,
    androidUrl
  );
  return (
    <noscript>
      <div style={{ backgroundColor: brandColors.offWhite, height: '100vh' }}>
        <ActionCards {...actionCardsProps} />
      </div>
    </noscript>
  );
}
